import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  HorizontalOriginOption,
  TextLinkKind,
  TextLinkWidget,
  VerticalOriginOption,
} from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { getAppStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../codegen/Localize';
import { ButtonWithUberDialog } from '../../common/react/components/ButtonWithUberDialog';
import { Alerts } from '../../dda/resources/Alerts';
import { ExtractTasks } from '../../extract/resources/ExtractTasks';
import { AlertErrorCode } from '../../ts/helpers/ErrorCodes';
import {
  actionsFooterStyles,
  asyncContentPlaceholderStyles,
  connectionDetailsStyles,
  notificationStyles,
  spinnerStyles
} from '../styles/AlertInfoStyles';
import { AlertInfoContent } from './AlertInfoContent';

export namespace TestIds {
  export const ALERT_ICON = 'alert-icon';
  export const CONNECTION_DETAILS = 'connection-details';
  export const NOTIFICATION = 'notification-text';
  export const SPINNER = 'requesting';
  export const TRY_AGAIN = 'try-again';
}

export const ZERO_WIDTH_SPACE = '\u200B';

export type AlertInfoProps = {
  id: string;
  type: string;
}

export function AlertInfo(props: AlertInfoProps): JSX.Element {
  let [alert, setAlert] = React.useState(undefined);
  let [isLoading, setIsLoading] = React.useState(true);
  let [tryAgain, setTryAgain] = React.useState(false);

  React.useEffect(
    () => {
      let isMounted = true;
      Alerts.getForType(props.type, props.id).then(alertListInfo => {
        if (isMounted && alertListInfo && alertListInfo.alerts && alertListInfo.alerts.length > 0) {
          setAlert(alertListInfo.alerts[0]);
          setIsLoading(false);
        }
      });
      return () => isMounted = false;
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleTryAgainClick = (taskId: string): void => {
    if (taskId) {
      ExtractTasks.run([taskId]);
      setTryAgain(prev => !prev);
    }
  };

  return <>
    <div style={asyncContentPlaceholderStyles}>
      {isLoading && <div style={spinnerStyles}>
        <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} testId={TestIds.SPINNER} />
      </div>}
      {!isLoading && <>
        <AlertInfoContent alert={alert} />
        {!isRemoteRefresh(alert.statusCode) &&
          <p data-tb-test-id={TestIds.NOTIFICATION} role={'alert'} style={notificationStyles}>
            { tryAgain ? Messages.refreshTaskRequested() : ZERO_WIDTH_SPACE }
          </p>}
      </>}
    </div>
    <div style={actionsFooterStyles}>
      <div style={connectionDetailsStyles}>
        <TextLinkWidget
          href={getConnectionDetailsUrl(props.type, props.id)}
          testId={TestIds.CONNECTION_DETAILS}
          type={TextLinkKind.LowEmphasis}>
          { Messages.connectionDetails() }
        </TextLinkWidget>
      </div>
      {!isLoading && !isRemoteRefresh(alert.statusCode) &&
        <ButtonWidget
          buttonType={ButtonType.Outline}
          disabled={tryAgain}
          handleClick={() => handleTryAgainClick(alert.taskId)}
          testId={TestIds.TRY_AGAIN}>
          { Messages.tryAgain() }
        </ButtonWidget>}
    </div>
  </>;
}

function isRemoteRefresh(statusCode: number): boolean {
  const REMOTE_REFRESH_STATUS_CODES: number[] = [
    AlertErrorCode.AlertRemoteUnknownFailure,
    AlertErrorCode.AlertRemoteSignInFailed,
    AlertErrorCode.AlertRemoteExpiredPassword,
    AlertErrorCode.AlertRemoteConnectionFailure,
    AlertErrorCode.AlertRemoteNoPassword,
    AlertErrorCode.AlertRemoteInvalidFilePath,
    AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime,
    AlertErrorCode.AlertRemoteBridgeClientNotRunning,
  ];
  return _.contains(REMOTE_REFRESH_STATUS_CODES, statusCode);
}

export function getConnectionDetailsUrl(type: string, id: string): string {
  if (type === 'datasource') {
    return datasourceConnectionsUrl(id);
  } else if (type === 'workbook') {
    return workbookUrl(id);
  }
  return '';
}

function workbookUrl(workbookId: string): string {
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  return siteUrls ? siteUrls.workbookDatasourcesUrl(workbookId) : '';
}

function datasourceConnectionsUrl(datasourceId: string): string {
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  return siteUrls ? siteUrls.datasourceConnectionsUrl(datasourceId) : '';
}



export type AlertIconWithUberProps = {
  id: string;
  type: string;
  iconButtonStyle?: React.CSSProperties;
  tabindex?: number;
}

export function AlertIconWithUber(props: AlertIconWithUberProps): JSX.Element {
  return <ButtonWithUberDialog
    dialogWidth={300}
    renderButton={showDialog => (
      <ButtonWidget
        buttonType={ButtonType.Icon}
        className={'tb-icon-alert'}
        onClick={showDialog}
        style={props.iconButtonStyle}
        tabIndex={props.tabindex ?? 0}
        testId={TestIds.ALERT_ICON}
        title={Messages.hasAlert()}
     />
    )}
    uberDialogContent={<AlertInfo id={props.id} type={props.type}/>}
    anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.bottom }}
    stackedOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top }}
  />;
}